
import React, { useState, useEffect, useRef } from "react";
import patientService from "../../../Services/patientService";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import doctorService from "../../../Services/doctorService";

const SectionLabel = ({ children }) => (
  <p className="text-[11px] uppercase tracking-widest font-[500] text-gray-400 mb-3 mt-6">
    {children}
  </p>
);

const InputField = ({ label, ...props }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[12px] font-[500] text-gray-500">{label}</label>
    <input
      {...props}
      className="w-full px-3 py-2 text-[13px] text-gray-800 bg-white border border-gray-200 rounded-lg outline-none  hover:border-orange-200 focus:border-orange-200 focus:ring-2 focus:ring-orange-100"
    />
  </div>
);

const ValidatedField = ({ label, value, maxLength, onChange, placeholder, hint }) => {
  const isEmpty = value.length === 0;
  const isComplete = value.length === maxLength;
  const isPartial = value.length > 0 && value.length < maxLength;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <label className="text-[12px] font-[500] text-gray-500">{label}</label>
        {isPartial && (
          <span className="text-[11px] text-red-400">{maxLength - value.length} digits remaining</span>
        )}
        {isComplete && (
          <span className="text-[11px] text-green-500 font-[500]">✓ Valid {hint}</span>
        )}
      </div>
      <input
        type="text"
        value={value}
        onChange={onChange}
        required
        maxLength={maxLength}
        placeholder={placeholder}
        className={`w-full px-3 py-2 text-[13px] text-gray-800 bg-white border rounded-lg outline-none shadow-sm transition-colors
          hover:border-orange-200 focus:ring-2 focus:ring-orange-100
          ${isComplete
            ? "border-green-400 focus:border-green-400"
            : isPartial
            ? "border-red-300 focus:border-orange-200"
            : "border-gray-200 focus:border-orange-200"
          }`}
      />
    </div>
  );
};

// ──  gender dropdown ──
const GenderSelect = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const options = ["Male", "Female", "Other"];

  return (
    <div className="flex flex-col gap-1">
      <label className="text-[12px] font-[500] text-gray-500">Gender</label>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className={`w-full px-3 py-2 text-[13px] text-left bg-white border rounded-lg outline-none shadow-sm transition-colors flex items-center justify-between
            hover:border-orange-200
            ${open ? "border-orange-200 ring-2 ring-orange-100" : "border-gray-200"}
            ${value ? "text-gray-800" : "text-gray-400"}`}
        >
          {value || "Select gender"}
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {open && (
          <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-md overflow-hidden">
            {options.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => {
                  onChange({ target: { name: "gender", value: opt } });
                  setOpen(false);
                }}
                className={`w-full px-3 py-2 text-[13px] text-left transition-colors
                  hover:bg-gray-50 hover:text-orange-600
                  ${value === opt ? "bg-orange-50 text-gray-600 font-[500]" : "text-gray-700"}`}
              >
                {opt}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const DOBInput = ({ value, onChange }) => {
  const dateInputRef = useRef(null);
  const [focused, setFocused] = useState(false);
  const [activeSegment, setActiveSegment] = useState(null);

  const parseISO = (iso) => {
    if (!iso) return { dd: "", mm: "", yyyy: "" };
    const [y, m, d] = iso.split("-");
    return { dd: d || "", mm: m || "", yyyy: y || "" };
  };

  const [segments, setSegments] = useState(() => parseISO(value));

  useEffect(() => {
    setSegments(parseISO(value));
  }, [value]);

  const pushChange = (dd, mm, yyyy) => {
    if (dd && mm && yyyy && yyyy.length === 4) {
      const iso = `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
      onChange({ target: { name: "date_of_birth", value: iso } });
    } else {
      onChange({ target: { name: "date_of_birth", value: "" } });
    }
  };

  const handleCalendarPick = (e) => {
    const iso = e.target.value;
    const parsed = parseISO(iso);
    setSegments(parsed);
    onChange({ target: { name: "date_of_birth", value: iso } });
  };

  const handleSegmentKeyDown = (e, segment) => {
    const { dd, mm, yyyy } = segments;

    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
      const delta = e.key === "ArrowUp" ? 1 : -1;

      if (segment === "dd") {
        let d = parseInt(dd || "1") + delta;
        if (d < 1) d = 31;
        if (d > 31) d = 1;
        const newDD = String(d).padStart(2, "0");
        setSegments((s) => ({ ...s, dd: newDD }));
        pushChange(newDD, mm, yyyy);
      } else if (segment === "mm") {
        let m = parseInt(mm || "1") + delta;
        if (m < 1) m = 12;
        if (m > 12) m = 1;
        const newMM = String(m).padStart(2, "0");
        setSegments((s) => ({ ...s, mm: newMM }));
        pushChange(dd, newMM, yyyy);
      } else if (segment === "yyyy") {
        let y = parseInt(yyyy || new Date().getFullYear()) + delta;
        if (y < 1900) y = 1900;
        if (y > 2100) y = 2100;
        const newYYYY = String(y);
        setSegments((s) => ({ ...s, yyyy: newYYYY }));
        pushChange(dd, mm, newYYYY);
      }
    }

    if (e.key === "Tab" || e.key === "/" || e.key === " ") {
      if (e.key !== "Tab") e.preventDefault();
      if (segment === "dd") document.getElementById("dob_mm")?.focus();
      if (segment === "mm") document.getElementById("dob_yyyy")?.focus();
    }

    if (e.key === "Backspace") {
      if (segment === "mm" && mm === "") {
        e.preventDefault();
        document.getElementById("dob_dd")?.focus();
      }
      if (segment === "yyyy" && yyyy === "") {
        e.preventDefault();
        document.getElementById("dob_mm")?.focus();
      }
    }
  };

  const handleDDChange = (e) => {
    const raw = e.target.value.replace(/\D/g, "").slice(0, 2);
    setSegments((s) => ({ ...s, dd: raw }));
    pushChange(raw, segments.mm, segments.yyyy);
    if (raw.length === 2) document.getElementById("dob_mm")?.focus();
  };

  const handleMMChange = (e) => {
    const raw = e.target.value.replace(/\D/g, "").slice(0, 2);
    setSegments((s) => ({ ...s, mm: raw }));
    pushChange(segments.dd, raw, segments.yyyy);
    if (raw.length === 2) document.getElementById("dob_yyyy")?.focus();
  };

  const handleYYYYChange = (e) => {
    const raw = e.target.value.replace(/\D/g, "").slice(0, 4);
    setSegments((s) => ({ ...s, yyyy: raw }));
    pushChange(segments.dd, segments.mm, raw);
  };

  const segmentClass = (isActive) =>
    `w-7 text-center bg-transparent outline-none text-[13px] text-gray-800 
     rounded px-0.5 transition-colors
     ${isActive ? "bg-orange-100 text-orange-700" : ""}`;

  return (
    <div className="flex flex-col gap-1">
      <label className="text-[12px] font-[500] text-gray-500">Date of Birth</label>
      <div
        className={`w-full px-3 py-2 text-[13px] text-gray-800 bg-white border rounded-lg shadow-sm 
          flex items-center justify-between transition-colors cursor-text
          hover:border-orange-200
          ${focused ? "border-orange-200 ring-2 ring-orange-100" : "border-gray-200"}`}
      >
        <div className="flex items-center gap-0.5 flex-1">
          {/* Day */}
          <input
            id="dob_dd"
            type="text"
            inputMode="numeric"
            placeholder="DD"
            value={segments.dd}
            onChange={handleDDChange}
            onKeyDown={(e) => handleSegmentKeyDown(e, "dd")}
            onFocus={() => { setFocused(true); setActiveSegment(0); }}
            onBlur={() => { setFocused(false); setActiveSegment(null); }}
            maxLength={2}
            className={segmentClass(activeSegment === 0)}
            style={{ width: "26px" }}
          />
          <span className="text-gray-400 select-none">/</span>

          {/* Month */}
          <input
            id="dob_mm"
            type="text"
            inputMode="numeric"
            placeholder="MM"
            value={segments.mm}
            onChange={handleMMChange}
            onKeyDown={(e) => handleSegmentKeyDown(e, "mm")}
            onFocus={() => { setFocused(true); setActiveSegment(1); }}
            onBlur={() => { setFocused(false); setActiveSegment(null); }}
            maxLength={2}
            className={segmentClass(activeSegment === 1)}
            style={{ width: "26px" }}
          />
          <span className="text-gray-400 select-none">/</span>

          {/* Year */}
          <input
            id="dob_yyyy"
            type="text"
            inputMode="numeric"
            placeholder="YYYY"
            value={segments.yyyy}
            onChange={handleYYYYChange}
            onKeyDown={(e) => handleSegmentKeyDown(e, "yyyy")}
            onFocus={() => { setFocused(true); setActiveSegment(2); }}
            onBlur={() => { setFocused(false); setActiveSegment(null); }}
            maxLength={4}
            className={segmentClass(activeSegment === 2)}
            style={{ width: "38px" }}
          />
        </div>

        {/* Calendar icon */}
        <button
          type="button"
          onClick={() => dateInputRef.current?.showPicker()}
          className="text-gray-400 hover:text-orange-400 transition-colors ml-1 flex-shrink-0"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </button>

        {/* Hidden native date picker */}
        <input
          ref={dateInputRef}
          type="date"
          value={value || ""}
          onChange={handleCalendarPick}
          tabIndex={-1}
          className="absolute w-0 h-0 opacity-0 pointer-events-none"
        />
      </div>
    </div>
  );
};

const AddEditPatient = () => {
  const [patientData, setPatientData] = useState({
    full_name: "",
    contact_number: "",
    gender: "",
    date_of_birth: "",
    address: "",
    checkup_date: new Date().toISOString().split("T")[0],
    CNIC: "",
  });

  const [doctorData, setDoctorData] = useState([]);
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      const fetchPatientData = async () => {
        try {
          const response = await patientService.fetchPatientById(id);
          setPatientData(response.patient);
        } catch (error) {
          toast.error("Error fetching patient data.");
        }
      };
      fetchPatientData();
    }
  }, [id]);

  useEffect(() => {
    const getDoctors = async () => {
      try {
        const response = await doctorService.fetchAllDoctors();
        setDoctorData(response.doctors);
      } catch (error) {
        toast.error("Error fetching Doctors");
      }
    };
    getDoctors();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPatientData({ ...patientData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (id) {
        await patientService.updatePatient(id, patientData);
        toast.success("Patient updated successfully!");
      } else {
        await patientService.create(patientData);
        toast.success("Please add Checkup now to Proceed!");
      }
      navigate("/checkup");
    } catch (error) {
      toast.error("Error saving patient.");
    }
  };

  return (
    <div className="bg-gray-50 flex items-start justify-center py-10 px-4">
      <div className="w-full max-w-7xl">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">

          {/* Header */}
          <div className="bg-gray-800 px-8 py-5">
            <h1 className="text-white text-[18px] font-[500] tracking-tight">
              {id ? "Edit Patient" : "New Patient Registration"}
            </h1>
            <p className="text-gray-400 text-[12px] mt-0.5">
              {id
                ? "Update the patient's details below"
                : "Fill in the details below to register a new patient"}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-8 py-6">

            <SectionLabel>Personal Information</SectionLabel>
            <div className="grid grid-cols-2 gap-4">
              <InputField
                label="Patient Name"
                type="text"
                name="full_name"
                value={patientData.full_name}
                onChange={handleChange}
                required
                placeholder="Enter full name"
              />
              <ValidatedField
                label="Phone Number"
                value={patientData.contact_number}
                maxLength={11}
                placeholder="e.g. 03001234567"
                hint="number"
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "").slice(0, 11);
                  setPatientData({ ...patientData, contact_number: val });
                }}
              />
            </div>

            <div className="grid grid-cols-3 gap-4 mt-4">
              {/* ── Updated DOB only ── */}
              <DOBInput
                value={patientData.date_of_birth}
                onChange={handleChange}
              />
              <GenderSelect value={patientData.gender} onChange={handleChange} />
              <InputField
                label="Register Date"
                type="date"
                name="checkup_date"
                value={patientData.checkup_date}
                onChange={handleChange}
                required
                readOnly
              />
            </div>

            <SectionLabel>Identity & Contact</SectionLabel>
            <div className="grid grid-cols-2 gap-4">
              <ValidatedField
                label="CNIC"
                value={patientData.CNIC}
                maxLength={13}
                placeholder="13 digits, numbers only"
                hint="CNIC"
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "").slice(0, 13);
                  setPatientData({ ...patientData, CNIC: val });
                }}
              />
              <InputField
                label="Residential Address"
                type="text"
                name="address"
                value={patientData.address}
                onChange={handleChange}
                required
                placeholder="Enter full address"
              />
            </div>

            {/* Footer */}
            <div className="border-t border-gray-100 mt-8 pt-6 flex items-center justify-between">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="text-[13px] text-gray-400 hover:text-gray-600 transition-colors font-[500]"
              >
                ← Cancel
              </button>
              <button
                type="submit"
                className="bg-gray-800 hover:bg-gray-700 transition-colors text-white text-[13px] font-[500] px-6 py-2.5 rounded-lg"
              >
                {id ? "Update Patient" : "Save & Continue →"}
              </button>
            </div>

          </form>
        </div>

        <p className="text-center text-[12px] text-gray-400 mt-4">
          {id ? "Changes will be saved immediately." : "Next step: Add checkup details after saving."}
        </p>
      </div>
    </div>
  );
};

export default AddEditPatient;